import React, { useState, useEffect, useRef } from 'react';
import Icon from './AppIcon';

/**
 * OfflineBanner
 * Renders a full-width sticky banner below the navbar when the browser is offline.
 * Shows a green "Conexión restaurada" banner for 3 seconds on reconnect.
 *
 * @param {{ onReconnect?: function }} props
 */
export default function OfflineBanner({ onReconnect }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showRestored, setShowRestored] = useState(false);
  const restoredTimerRef = useRef(null);
  const wasOfflineRef = useRef(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (wasOfflineRef?.current) {
        setShowRestored(true);
        onReconnect?.();
        restoredTimerRef.current = setTimeout(() => {
          setShowRestored(false);
        }, 3000);
      }
      wasOfflineRef.current = false;
    };

    const handleOffline = () => {
      setIsOnline(false);
      wasOfflineRef.current = true;
      setShowRestored(false);
      if (restoredTimerRef?.current) {
        clearTimeout(restoredTimerRef?.current);
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (restoredTimerRef?.current) clearTimeout(restoredTimerRef?.current);
    };
  }, [onReconnect]);

  if (isOnline && !showRestored) return null;

  if (showRestored) {
    return (
      <div
        className="fixed top-[60px] left-0 right-0 z-[200] flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-500 text-white text-sm font-medium shadow-md"
        style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
      >
        <Icon name="Wifi" size={16} color="currentColor" />
        <span>Conexión restaurada</span>
      </div>
    );
  }

  return (
    <div
      className="fixed top-[60px] left-0 right-0 z-[200] flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-500 text-white text-sm font-medium shadow-md"
      style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
    >
      <Icon name="WifiOff" size={16} color="currentColor" />
      <span>Sin conexión a Internet. Los cambios no se guardarán hasta recuperar la conexión.</span>
    </div>
  );
}
